import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const IBAN = "IT50R0307502200CC8500900414";
const IBAN_DISPLAY = "IT50 R030 7502 200C C850 0900 414";

const GIFT_OPTIONS = [
  {
    id: "viaggio",
    image: "https://customer-assets.emergentagent.com/job_guest-connect-16/artifacts/ymewaqb9_viaggio.jpeg",
    title: "Gocce di miele",
    text: "Non avremo una luna di miele… ne avremo cento! Una fuga romantica alla volta, assaporeremo il mondo una goccia dopo l'altra. Ogni contributo è un tramonto in più da vivere insieme.",
    causale: "Goccie di miele",
  },
  {
    id: "terrazzo",
    image: "https://customer-assets.emergentagent.com/job_guest-connect-16/artifacts/v9v1w234_terrazzo.jpeg",
    title: "Il nostro terrazzo in fiore",
    text: "Sognamo un angolo verde tutto nostro: un posto dove fare colazione al sole, cenare sotto le stelle e coltivare insieme qualcosa di bello, soprattutto potervi accogliere per un aperitivo.",
    causale: "Il nostro terrazzo in fiore",
  },
  {
    id: "beneficenza",
    image: "https://customer-assets.emergentagent.com/job_guest-connect-16/artifacts/9wmnf2aj_beneficenza.jpeg",
    title: "Un gesto che scalda il cuore",
    text: "Il regalo più bello che ci possiate fare è condividere il nostro impegno: ogni contributo andrà ai senza tetto di cui ci occupiamo, distribuendo beni di prima necessità attraverso la nostra associazione VOCI Volontari Cittadini. Se volete potete partecipare alla distribuzione di quanto donato in una delle nostre serate.",
    causale: "Un gesto che scalda il cuore",
  },
];

export default function RegistryPage() {
  const [copiedId, setCopiedId] = useState(null);

  const copyIBAN = async (causale, id) => {
    try {
      await navigator.clipboard.writeText(IBAN);
      setCopiedId(id);
      toast.success(`IBAN copiato! Usa come causale: "${causale}"`);
      setTimeout(() => setCopiedId(null), 3000);
    } catch {
      toast.error("Impossibile copiare. Copia manualmente.");
    }
  };

  return (
    <main
      data-testid="registry-page"
      className="min-h-screen pt-20 pb-16 bg-warm-white"
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center py-10">
          <h1
            className="font-serif text-4xl sm:text-5xl mb-2"
            style={{ color: "#2C2C2C" }}
          >
            Lista Nozze
          </h1>
          <div className="gold-line mb-4" />
          <p className="text-base" style={{ color: "#666666" }}>
            Il regalo più bello è la vostra presenza
          </p>
        </div>

        {/* Message card */}
        <div
          className="bg-white rounded-sm p-8 mb-12 text-center max-w-2xl mx-auto"
          style={{ border: "1px solid rgba(197, 165, 114, 0.3)" }}
        >
          <Heart
            size={32}
            className="mx-auto mb-4"
            style={{ color: "#C5A572" }}
          />
          <p
            className="text-base leading-relaxed mb-2"
            style={{ color: "#2C2C2C" }}
          >
            Carissimi, la vostra presenza al nostro matrimonio è per noi il
            dono più grande. Se desiderate farci un regalo, scegliete la causa
            che vi sta più a cuore e contribuite tramite bonifico bancario.
          </p>
          <p className="text-sm italic" style={{ color: "#666666" }}>
            Con tutto il nostro amore, Marianna & Marco
          </p>
        </div>

        {/* Three columns */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {GIFT_OPTIONS.map((opt) => (
            <div
              key={opt.id}
              data-testid={`gift-option-${opt.id}`}
              className="bg-white rounded-sm overflow-hidden flex flex-col"
              style={{ border: "1px solid rgba(197, 165, 114, 0.3)" }}
            >
              {/* Image */}
              <div className="flex items-center justify-center p-6" style={{ backgroundColor: "#FFFDF8" }}>
                <img
                  src={opt.image}
                  alt={opt.title}
                  className="w-48 h-48 object-contain"
                  loading="lazy"
                />
              </div>

              {/* Content */}
              <div className="p-6 flex flex-col flex-1 text-center">
                <h3
                  className="font-serif text-xl mb-3"
                  style={{ color: "#2C2C2C" }}
                >
                  {opt.title}
                </h3>
                <p
                  className="text-sm leading-relaxed flex-1 mb-5"
                  style={{ color: "#666666" }}
                >
                  {opt.text}
                </p>

                <Separator className="mb-4" />

                {/* IBAN + Copy */}
                <p className="text-xs mb-1" style={{ color: "#666666" }}>
                  Intestazione
                </p>
                <p
                  className="text-sm font-medium mb-3"
                  style={{ color: "#2C2C2C" }}
                >
                  Marianna Cerri Tissino e Marco Musso
                </p>
                <p className="text-xs mb-1" style={{ color: "#666666" }}>
                  IBAN
                </p>
                <p
                  className="font-mono text-xs sm:text-sm font-medium mb-1 tracking-wider"
                  style={{ color: "#2C2C2C" }}
                >
                  {IBAN_DISPLAY}
                </p>
                <p className="text-xs mb-3" style={{ color: "#8B9A6B" }}>
                  Causale: "{opt.causale}"
                </p>
                <Button
                  data-testid={`copy-iban-${opt.id}`}
                  className="btn-press touch-target rounded-sm w-full"
                  style={{
                    backgroundColor: copiedId === opt.id ? "#8B9A6B" : "#C5A572",
                    color: "white",
                    minHeight: "44px",
                  }}
                  onClick={() => copyIBAN(opt.causale, opt.id)}
                >
                  {copiedId === opt.id ? (
                    <>
                      <Check size={16} className="mr-2" /> Copiato!
                    </>
                  ) : (
                    <>
                      <Copy size={16} className="mr-2" /> Copia IBAN
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
